import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Copy, GraduationCap, CheckCircle2,
  Loader2, Send, Calendar as CalendarIcon, ArrowRight, UserCheck, Briefcase,
  Bell, Clock, ChevronRight, Video, Building2, ShieldCheck,
} from 'lucide-react';
import { isOfflineMode, formatDay, formatTime } from './AssignMentorDialog';
import { SC as STATUS_COLORS, STATUS_LABEL } from './ExperienceTable';
import { toast } from 'sonner';
import { PARTICIPATION_AGREEMENT, SAFEGUARDING_AGREEMENT } from '@/data/agreements';
import { AgreementModal } from '@/components/AgreementModal';
import { PrincipalCertificateCard } from './PrincipalCertificateCard';
import { InternshipPlacementCard } from './InternshipPlacementCard';
import { boardLabel } from '@/data/educationBoards';

// ─────────────────── OVERVIEW TAB ───────────────────
const EXP_TYPES = [
  { id: 'online', label: 'Online Sessions', icon: Video, section: 'exp_online' },
  { id: 'offline', label: 'Workplace Visits', icon: Building2, section: 'exp_visits' },
  { id: 'internships', label: 'Internships', icon: Briefcase, section: 'exp_internships' },
];

/** School-wide coordinator overview across online sessions, workplace visits and internships. */
export function OverviewTab({ stats, bookings, user, onNavigate, attention, interns, safeguarding }) {
  const [tasks, setTasks] = useState({ reschedules: [] });
  const [gaps, setGaps] = useState(null);

  const loadTasks = useCallback(async () => {
    const [resched, coverage] = await Promise.allSettled([
      api.get('/coordinator/reschedule-requests'),
      api.get('/coordinator/career-coverage'),
    ]);
    setGaps(coverage.status === 'fulfilled' ? (coverage.value.data?.gaps || null) : null);
    setTasks({ reschedules: resched.status === 'fulfilled' ? (resched.value.data || []) : [] });
  }, []);
  useEffect(() => { loadTasks(); }, [loadTasks]);

  const ofType = (b, offline) => isOfflineMode(b.assigned_mode || b.requested_mode) === offline;
  const todayStr = new Date().toISOString().split('T')[0];
  const isUpcoming = b => ['confirmed', 'pending_parent_confirmation'].includes(b.status)
    && b.assigned_date && b.assigned_date >= todayStr;
  const upcoming = bookings.filter(isUpcoming);
  // Conducted and verified; waiting on family feedback still counts as conducted
  const completed = bookings.filter(b => ['completed', 'awaiting_feedback'].includes(b.status) && b.completed_at);
  const adminClosed = bookings.filter(b => !!b.admin_closed_at);
  // A grouped session is one experience; each attended student is one participation
  const experienceCount = list => new Set(list.map(b => b.session_id || b.id)).size;
  const participationCount = list => list.filter(b => b.attended === true).length;

  const im = interns?.metrics || {};
  const internUpcoming = im.upcoming_placements || 0;
  const internCompleted = im.completed || 0;
  const goInternship = (p, tab, focus) => onNavigate('exp_internships',
    { url: { int: 'programs', ip: p, it: tab, f: focus } });

  // Same source as the sidebar badges, so the two can never disagree
  const actions = (attention?.rows || []).map(r => ({
    ...r, go: () => onNavigate(r.section, r.opts),
  }));
  const pendingActions = attention?.total || 0;

  const internActions = attention?.internActions || [];
  const safeguardingItems = safeguarding?.items || [];
  const internWaiting = (interns?.needs_attention || []).filter(x => x.group === 'waiting');

  const metrics = [
    { key: 'active-students', label: 'Active Students', value: stats?.active_children || 0, icon: UserCheck, color: '#10B981', go: () => onNavigate('students') },
    { key: 'pending-actions', label: 'Needs Your Action', value: pendingActions, icon: Bell, color: '#F97D6A', go: () => onNavigate('tasks') },
    { key: 'upcoming-experiences', label: 'Upcoming Experiences', value: experienceCount(upcoming) + internUpcoming, icon: CalendarIcon, color: '#8B5CF6', go: () => onNavigate('upcoming') },
    { key: 'completed-experiences', label: 'Completed Experiences', value: experienceCount(completed) + internCompleted, icon: CheckCircle2, color: '#14B8A6', go: () => onNavigate('completed'),
      note: `${participationCount(completed) + internCompleted} student participation${participationCount(completed) + internCompleted === 1 ? '' : 's'}` },
  ];

  const byType = EXP_TYPES.map(t => (t.id === 'internships'
    ? { ...t, upcoming: internUpcoming, completed: internCompleted,
      goUp: () => onNavigate('exp_internships', { url: { int: 'programs', m: 'placed' } }),
      goDone: () => onNavigate('exp_internships', { url: { int: 'programs', m: 'completed' } }) }
    : {
      ...t,
      upcoming: experienceCount(upcoming.filter(b => ofType(b, t.id === 'offline'))),
      completed: experienceCount(completed.filter(b => ofType(b, t.id === 'offline'))),
      goUp: () => onNavigate('upcoming', { mode: t.id, url: { mode: t.id } }),
      goDone: () => onNavigate('completed', { mode: t.id, url: { mode: t.id } }),
    }));

  const coverageGaps = gaps ? [
    { id: 'no_cover', label: 'Asked for, nobody covers it', count: gaps.no_cover },
    { id: 'no_times', label: 'Has a mentor, but no times published', count: gaps.no_times },
    { id: 'single_mentor', label: 'Only one mentor deep', count: gaps.single_mentor },
  ].filter(g => g.count > 0) : [];

  return (
    <div className="space-y-6" data-testid="overview-tab">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-[#1F2937] font-['Outfit'] tracking-tight" data-testid="overview-greeting">
            Hello, {(user?.full_name || '').trim().split(/\s+/)[0] || 'there'}!
          </h2>
          <p className="text-sm text-[#9CA3AF] mt-1" data-testid="overview-greeting-subtitle">Let's help more students explore their futures.</p>
        </div>
        <Button size="sm" onClick={() => onNavigate('calendar')} className="rounded-lg text-xs bg-[#1F2937] text-white hover:bg-[#111827]" data-testid="open-school-calendar">
          <CalendarIcon className="h-3.5 w-3.5 mr-1.5" />School Calendar
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="overview-metrics">
        {metrics.map(m => (
          <button key={m.key} onClick={m.go} className="h-full flex flex-col justify-between bg-white rounded-xl border border-[#E5E7EB] p-4 text-left hover:shadow-sm hover:border-[#F97D6A] transition-all" data-testid={`metric-${m.key}`}>
            <div className="flex items-start gap-2 mb-2 min-h-[2.1rem]">
              <m.icon className="h-4 w-4 mt-px shrink-0" style={{ color: m.color }} />
              <span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider leading-tight">{m.label}</span>
            </div>
            <p className="text-2xl font-extrabold text-[#1F2937] font-['Outfit']">{m.value}</p>
            {m.note && <p className="text-[10px] text-[#9CA3AF] mt-0.5" data-testid={`metric-note-${m.key}`}>{m.note}</p>}
          </button>
        ))}
      </div>

      <section data-testid="needs-attention">
        <h3 className="text-sm font-bold text-[#1F2937] mb-2.5 font-['Outfit'] flex items-center gap-1.5">
          <Bell className="h-3.5 w-3.5 text-[#F97D6A]" />Needs your action
        </h3>
        {actions.length === 0 && internActions.length === 0 && safeguardingItems.length === 0 ? (
          <p className="bg-white rounded-xl border border-[#E5E7EB] px-4 py-5 text-sm text-[#9CA3AF] text-center" data-testid="needs-attention-empty">
            Nothing waiting on you right now.
          </p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-2">
            {safeguardingItems.map(item => (
              <button
                key={item.id}
                onClick={() => onNavigate('safeguarding', { reportId: item.id })}
                className="w-full flex items-center justify-between gap-2 bg-[#FFF8F7] border border-[#FBD9D2] rounded-xl px-3.5 py-3 text-left hover:border-[#F97D6A] transition-colors group"
                data-testid={`action-safeguarding-${item.reference}`}
              >
                <span className="min-w-0">
                  <span className="text-sm text-[#4B5563] flex items-center gap-1.5">
                    <ShieldCheck className="h-3.5 w-3.5 text-[#F97D6A] shrink-0" />
                    <span className="font-semibold text-[#1F2937]">{item.reference}</span>
                  </span>
                  <span className="block text-xs text-[#6B7280] mt-0.5 break-words">{item.reason}</span>
                </span>
                <ArrowRight className="h-3.5 w-3.5 text-[#9CA3AF] group-hover:text-[#F97D6A] transition-colors shrink-0" />
              </button>
            ))}
            {actions.map(item => (
              <button key={item.id} onClick={item.go} className="w-full flex items-center justify-between bg-white border border-[#E5E7EB] rounded-xl px-3.5 py-3 text-left hover:border-[#F97D6A] transition-colors group" data-testid={`action-${item.id}`}>
                <span className="text-sm text-[#4B5563]">{item.label}</span>
                <span className="flex items-center gap-1.5 shrink-0">
                  <span className="text-xs font-bold text-[#F06651] bg-[#FFF0ED] rounded-full px-2 py-0.5">{item.count}</span>
                  <ArrowRight className="h-3.5 w-3.5 text-[#9CA3AF] group-hover:text-[#F97D6A] transition-colors" />
                </span>
              </button>
            ))}
            {internActions.map(item => (
              <ActionWithPrograms key={item.kind} item={item} onOpen={goInternship} testId={`action-intern-${item.kind}`} />
            ))}
          </div>
        )}
      </section>

      {internWaiting.length > 0 && (
        <section data-testid="waiting-on-others">
          <h3 className="text-sm font-bold text-[#6B7280] mb-2.5 font-['Outfit'] flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5 text-[#9CA3AF]" />Waiting on others
          </h3>
          <div className="grid sm:grid-cols-2 gap-2">
            {internWaiting.map(item => (
              <ActionWithPrograms key={item.kind} item={item} onOpen={goInternship} muted testId={`waiting-intern-${item.kind}`} />
            ))}
          </div>
        </section>
      )}

      <section data-testid="experiences-by-type">
        <h3 className="text-sm font-bold text-[#1F2937] mb-2.5 font-['Outfit']">Experiences by type</h3>
        <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-[#9CA3AF] border-b border-[#F3F4F6]">
                <th className="px-4 py-2.5 font-semibold">Experience type</th>
                <th className="px-3 py-2.5 font-semibold text-right">Upcoming</th>
                <th className="px-3 py-2.5 font-semibold text-right">Completed</th>
              </tr>
            </thead>
            <tbody>
              {byType.map(t => (
                <tr key={t.id} className="border-b border-[#F7F8F9] last:border-0" data-testid={`exp-type-${t.id}`}>
                  <td className="px-4 py-2.5">
                    <button onClick={() => onNavigate(t.section)} className="text-sm font-medium text-[#1F2937] hover:text-[#F06651] transition-colors flex items-center gap-2" data-testid={`exp-type-link-${t.id}`}>
                      <t.icon className="h-3.5 w-3.5 text-[#9CA3AF]" />{t.label}
                    </button>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <button onClick={t.goUp} disabled={!t.upcoming} className="text-sm font-semibold text-[#1F2937] hover:text-[#F06651] disabled:text-[#9CA3AF] disabled:cursor-default transition-colors" data-testid={`exp-upcoming-${t.id}`}>
                      {t.upcoming}
                    </button>
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <button onClick={t.goDone} disabled={!t.completed} className="text-sm font-semibold text-[#1F2937] hover:text-[#F06651] disabled:text-[#9CA3AF] disabled:cursor-default transition-colors" data-testid={`exp-completed-${t.id}`}>
                      {t.completed}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {(coverageGaps.length > 0 || adminClosed.length > 0) && (
        <section className="grid sm:grid-cols-2 gap-3">
          {coverageGaps.length > 0 && (
            <div className="bg-white rounded-xl border border-[#E5E7EB] p-4" data-testid="coverage-gaps-card">
              <h4 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider mb-1">Career coverage</h4>
              <div className="space-y-1.5">
                {coverageGaps.map(g => (
                  <button key={g.id} onClick={() => onNavigate('coverage', { gap: g.id })}
                    className="w-full flex items-center justify-between text-left py-1 group" data-testid={`coverage-gap-${g.id}`}>
                    <span className="text-sm text-[#6B7280]">{g.label}</span>
                    <span className="text-xs text-[#9CA3AF] flex items-center gap-1">
                      {g.count}<ChevronRight className="h-3 w-3 group-hover:text-[#F97D6A]" />
                    </span>
                  </button>
                ))}
              </div>
              <button onClick={() => onNavigate('coverage')} className="mt-2 text-[11px] text-[#F97D6A] hover:text-[#F06651] font-medium flex items-center gap-1" data-testid="coverage-view-all">
                See career coverage <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          )}
          {adminClosed.length > 0 && (
            <div className="bg-white rounded-xl border border-[#E5E7EB] p-4" data-testid="follow-ups">
              <h4 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider mb-1">Closed off</h4>
              <button onClick={() => onNavigate('admin_closed')} className="w-full flex items-center justify-between text-left py-1.5 group" data-testid="followup-admin-closed">
                <span className="text-sm text-[#6B7280] flex items-center gap-1.5"><Clock className="h-3 w-3 text-[#C8CDD6]" />Closed administratively</span>
                <span className="text-xs text-[#9CA3AF] flex items-center gap-1">{adminClosed.length}<ChevronRight className="h-3 w-3 group-hover:text-[#F97D6A]" /></span>
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

/** An internship action with one chip per programme, so a click lands on the right record. */
function ActionWithPrograms({ item, onOpen, muted, testId }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl px-3.5 py-3" data-testid={testId}>
      <p className="text-sm text-[#4B5563] flex items-center justify-between gap-2">
        {item.label}
        <span className={`text-xs font-bold rounded-full px-2 py-0.5 shrink-0 ${muted ? 'bg-[#F5F6F8] text-[#6B7280]' : 'text-[#F06651] bg-[#FFF0ED]'}`}>{item.count}</span>
      </p>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {item.programs.map(p => (
          <button key={p.program_id} onClick={() => onOpen(p.program_id, item.tab, item.kind)}
            className="text-[11px] font-medium text-[#4B5563] bg-[#F5F6F8] hover:bg-[#FFF0ED] hover:text-[#F06651] rounded-lg px-2.5 py-1 transition-colors"
            data-testid={`${testId}-${p.program_id}`}>
            {p.program_name} · {p.count}
          </button>
        ))}
      </div>
    </div>
  );
}


/** Internship work shown alongside the booking-only list pages, so counts never look wrong. */
export function InternshipListExtras({ kind, onNavigate }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/coordinator/internship-dashboard').then(r => setData(r.data)).catch(() => setData(null));
  }, []);
  if (!data) return null;
  const goInternship = (p, tab, focus) => onNavigate('exp_internships',
    { url: { int: 'programs', ip: p, it: tab, f: focus } });

  if (kind === 'tasks') {
    const items = (data.needs_attention || []).filter(x => x.group === 'your_action');
    if (!items.length) return null;
    return (
      <div className="mb-5" data-testid="task-internship-actions">
        <h4 className="text-xs font-bold text-[#9CA3AF] uppercase tracking-wider mb-1">Internship actions</h4>
        <div className="grid sm:grid-cols-2 gap-2">
          {items.map(item => (
            <ActionWithPrograms key={item.kind} item={item} onOpen={goInternship} testId={`task-intern-${item.kind}`} />
          ))}
        </div>
      </div>
    );
  }

  const count = kind === 'upcoming'
    ? (data.metrics?.upcoming_placements || 0) : (data.metrics?.completed || 0);
  if (!count) return null;
  return (
    <button onClick={() => onNavigate('exp_internships', { url: { int: 'programs', m: kind === 'upcoming' ? 'placed' : 'completed' } })}
      className="mb-4 w-full sm:w-auto flex items-center justify-between gap-3 bg-white border border-[#E5E7EB] rounded-xl px-3.5 py-2.5 text-left hover:border-[#F97D6A] transition-colors"
      data-testid={`list-internship-note-${kind}`}>
      <span className="text-sm text-[#4B5563]">
        Plus {count} internship placement{count === 1 ? '' : 's'} {kind === 'upcoming' ? 'still to run' : 'completed and verified'}
      </span>
      <ArrowRight className="h-3.5 w-3.5 text-[#9CA3AF]" />
    </button>
  );
}

// ─────────────────── SCHOOL SETTINGS TAB ───────────────────
export function SchoolSettingsTab({ school }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewAgreement, setViewAgreement] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get('/coordinator/school-agreements')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const copyCode = () => {
    if (!school?.invite_code) return;
    navigator.clipboard.writeText(school.invite_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const agreements = data?.agreements || {};
  const items = [
    { key: 'participation', def: PARTICIPATION_AGREEMENT, record: agreements.participation },
    { key: 'safeguarding', def: SAFEGUARDING_AGREEMENT, record: agreements.safeguarding },
  ];

  return (
    <div className="space-y-6" data-testid="school-settings-tab">
      {/* School Info */}
      <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider">School Name</span>
            <p className="text-[#1F2937] font-semibold mt-0.5">{school?.school_name || '—'}</p>
          </div>
          <div>
            <span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider">School ID</span>
            <p className="text-[#1F2937] font-semibold mt-0.5">{school?.school_id || '—'}</p>
          </div>
          <div>
            <span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider">Education Board</span>
            <p className="text-[#1F2937] font-semibold mt-0.5" data-testid="settings-education-board">{boardLabel(school)}</p>
            <p className="text-[10px] text-[#9CA3AF] mt-0.5">Managed by CareersPOV — raise a ticket to change it</p>
          </div>
          <div className="sm:col-span-2">
            <span className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider">School Invite Code</span>
            <p className="text-[10px] text-[#9CA3AF] mt-0.5 mb-1">Reserved for future enrolment options</p>
            <div className="flex items-center gap-2">
              <code className="text-sm font-mono font-bold text-[#1F2937] bg-[#F8F9FA] rounded-lg px-3 py-1.5" data-testid="settings-invite-code">{school?.invite_code || '—'}</code>
              <Button variant="ghost" size="sm" onClick={copyCode} className="h-8 text-xs rounded-lg hover:bg-[#FFF0ED]">
                <Copy className="h-3.5 w-3.5 mr-1" />{copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <PrincipalCertificateCard />
      <InternshipPlacementCard />

      {/* Agreements */}
      <div>
        <h3 className="text-sm font-bold text-[#1F2937] mb-3 font-['Outfit']">School Agreements</h3>
        {loading ? (
          <div className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[#9CA3AF]" /></div>
        ) : (
          <div className="space-y-3">
            {items.map(({ key, def, record }) => {
              const isPlaceholder = record?.version?.includes('ABCD') || record?.is_test_acceptance;
              return (
                <div key={key} className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-[#1F2937]">{def.title}</h4>
                      {record?.accepted ? (
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={`border-0 text-[10px] ${isPlaceholder ? 'bg-red-50 text-red-500' : 'bg-emerald-50 text-emerald-600'}`}>
                            {isPlaceholder ? 'Placeholder' : `Version ${record.version}`}
                          </Badge>
                          <span className="text-xs text-[#9CA3AF]">Accepted {new Date(record.accepted_at).toLocaleString()}</span>
                        </div>
                      ) : (
                        <p className="text-xs text-amber-500 mt-1">Not yet accepted</p>
                      )}
                    </div>
                    {record?.accepted && !isPlaceholder && (
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" className="text-xs rounded-lg" onClick={() => setViewAgreement({ key, record })} data-testid={`view-agreement-${key}`}>
                          View
                        </Button>
                        <a href={`/api/agreements/${key}/pdf?school_id=${school?.school_id}`} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center h-8 px-3 text-xs rounded-lg border border-[#E5E7EB] text-[#6B7280] hover:bg-[#F8F9FA] font-medium" data-testid={`download-agreement-${key}`}>
                          PDF
                        </a>
                      </div>
                    )}
                  </div>
                  {record?.accepted && (
                    <div className="mt-3 text-sm text-[#6B7280] grid grid-cols-2 gap-x-6 gap-y-1">
                      <div>Signatory: <span className="text-[#1F2937] font-medium">{record.signatory_name}</span></div>
                      <div>Designation: <span className="text-[#1F2937] font-medium">{record.signatory_designation}</span></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {viewAgreement && (
        <AgreementModal
          open={!!viewAgreement}
          onClose={() => setViewAgreement(null)}
          agreement={viewAgreement.key === 'participation' ? PARTICIPATION_AGREEMENT : SAFEGUARDING_AGREEMENT}
          acceptanceRecord={viewAgreement.record}
        />
      )}
    </div>
  );
}
